import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { subscribeCustomLobbies, type CustomLobby } from "../../online/net";
import { genLobbyCode } from "../lobby/lobby";
import { arenaLabel, mapValueOf, shapeAndSizeOf } from "./maps";
import { B, Globe, Hex, Keys, Lock, MapGlyph, Plus, RoomsPage, useLand } from "./RoomsUI";
import { useIsTouch } from "../../shared/useResponsive";
import s from "./rooms.module.scss";

interface Props {
  onBack?: () => void;
}

type Step = "home" | "kind";
type Kind = "public" | "private";

const cleanCode = (c: string) => c.trim().toUpperCase().slice(0, 8);

// Game Rooms, the front door (/join): join by code, drop into an open room, or
// create one. Creating is one choice, who it is for (Public / Private / Local),
// and the room opens on the Classic rules (changed in the room itself). The
// room lives at /lobby/CODE and opens the moment its page does, with whoever
// opens it as the host; the choice rides along as router state (public).
// /join?create=public|private goes straight to a new room.
export default function JoinLobbyPage({ onBack }: Props) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const land = useLand();
  const touch = useIsTouch();
  const preset = params.get("create");
  const [step, setStep] = useState<Step>("home");
  const [lobbies, setLobbies] = useState<CustomLobby[]>([]);
  const [code, setCode] = useState("");

  // Back-compat: an old /join?code=CODE invite link forwards to the room's URL.
  useEffect(() => {
    const c = params.get("code");
    if (c) navigate(`/lobby/${cleanCode(c)}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => subscribeCustomLobbies((l) => setLobbies(l)), []);

  const join = (c: string) => { const clean = cleanCode(c); if (clean) navigate(`/lobby/${clean}`); };
  const create = (kind: Kind, replace = false) => navigate(`/lobby/${genLobbyCode()}`, { state: { public: kind === "public", flavour: "classic" }, replace });
  // /join?create=public|private (the start page's Create room): straight in.
  useEffect(() => {
    if (preset === "public" || preset === "private") create(preset, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const back = () => {
    if (step === "kind") setStep("home");
    else onBack?.();
  };

  const home = (
    <div className={s.home}>
      <div className={s.homeLeft}>
        <div className={s.label}>Join with a code</div>
        <form className={s.codeRow} onSubmit={(e) => { e.preventDefault(); join(code); }}>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))} placeholder="CODE" aria-label="Room code" autoComplete="off" autoCapitalize="characters" spellCheck={false} />
          <B type="submit" disabled={!code.trim()}>Join</B>
        </form>
        <div className={s.or}><span>or</span></div>
        <B className={s.createBtn} onClick={() => setStep("kind")}><Plus />Create room</B>
        <div className={s.note}>{touch ? "Public or private" : "Public, private or on this device"}</div>
      </div>
      <div className={s.homeRight}>
        <div className={s.label}>Open rooms</div>
        {lobbies.length === 0 ? (
          <div className={s.noRooms}>
            <b>No open rooms right now</b>
          </div>
        ) : (
          <div className={s.rooms}>
            {lobbies.map((o, i) => {
              const full = o.players >= o.maxPlayers;
              const a = o.map ? shapeAndSizeOf(mapValueOf(o.map)) : null;
              return (
                <B key={o.code} className={s.roomCard} style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} disabled={full} onClick={() => join(o.code)} aria-label={`Join ${o.hostName || "this"} room`}>
                  <span className={s.roomText}>
                    <b>{o.name || (o.hostName ? `${o.hostName}'s room` : "Open room")}</b>
                    <small><span>{o.map ? arenaLabel(mapValueOf(o.map)) + " · " : ""}{o.players} of {o.maxPlayers}</span>{full && <em className={s.started}>· Full</em>}</small>
                  </span>
                  {a && <span className={s.roomMap}><MapGlyph shape={a.shape} /></span>}
                </B>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const kinds: [Kind | "local", string, string, () => React.ReactElement][] = [
    ["public", "Public", "Shows up in open rooms. Anyone can join.", Globe],
    ["private", "Private", "Only friends with your code or link.", Lock],
    ["local", "Local", "Everyone on this keyboard.", Keys],
  ];
  // A local game needs a keyboard, so a touch device is not offered one.
  const offered = kinds.filter(([k]) => k !== "local" || !touch);
  const kindScreen = (
    <div className={s.kinds} style={{ gridTemplateColumns: `repeat(${offered.length}, 1fr)` }}>
      {offered.map(([k, name, line, I], i) => (
        <B key={k} className={s.kindCard} style={{ animationDelay: `${i * 60}ms` }} onClick={() => { if (k === "local") navigate("/local"); else create(k); }}>
          <Hex className={s.kindHex}><I /></Hex>
          <b>{name}</b>
          <small>{line}</small>
        </B>
      ))}
    </div>
  );

  const title: Record<Step, [string, string]> = {
    home: ["Game Rooms", land ? "Play with friends, or join an open room" : "Play with friends, or drop into an open room"],
    kind: ["Create a room", "Who is it for?"],
  };

  return (
    <RoomsPage title={title[step][0]} sub={title[step][1]} onBack={back} stageKey={step}>
      {step === "home" && home}
      {step === "kind" && kindScreen}
    </RoomsPage>
  );
}
